/**
 * Admin panel route error boundary. Mirrors the dashboard version so that
 * any thrown React render — including DOM-mutation crashes triggered by
 * translate extensions or Socket events arriving mid-unmount — is caught
 * and presented with a usable recovery screen instead of the default
 * "💿 Hey developer" React Router fallback.
 */
import { useRouteError, isRouteErrorResponse, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Logo } from "@/components/logo";
import { AlertTriangle, RefreshCw, Home } from "lucide-react";

export function RouteErrorBoundary() {
  const error = useRouteError();
  const navigate = useNavigate();
  const isDev = import.meta.env.DEV;

  let title = "Beklenmedik bir hata oluştu";
  let detail = "Sayfa yüklenirken bir sorun oluştu. Lütfen tekrar deneyin.";

  if (isRouteErrorResponse(error)) {
    title = `${error.status} ${error.statusText || "Hata"}`;
    detail = typeof error.data === "string" ? error.data : detail;
  } else if (error instanceof Error) {
    detail = error.message || detail;
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-background p-4">
      <div className="w-full max-w-md">
        <Logo size="lg" variant="full" suffix="Admin" className="mx-auto mb-6" />
        <div className="rounded-2xl border bg-card p-8 shadow-sm text-center">
          <div className="mx-auto mb-4 flex size-12 items-center justify-center rounded-full bg-destructive/10">
            <AlertTriangle className="size-6 text-destructive" />
          </div>
          <h1 className="mb-2 text-lg font-bold">{title}</h1>
          <p className="mb-6 text-sm text-muted-foreground">{detail}</p>
          {isDev && error instanceof Error && error.stack && (
            <pre className="mb-6 max-h-48 overflow-auto rounded-lg bg-slate-900 p-3 text-left text-[11px] leading-snug text-slate-200">
              {error.stack}
            </pre>
          )}
          <div className="flex gap-2">
            <Button onClick={() => window.location.reload()} className="flex-1">
              <RefreshCw className="size-4 mr-2" />
              Yenile
            </Button>
            <Button variant="outline" onClick={() => navigate("/")} className="flex-1">
              <Home className="size-4 mr-2" />
              Ana sayfa
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
